import * as THREE from 'three'
import { applyMeshCoordinateOptions } from '@/plugins/mesh/applyMeshCoordinateOptions'
import { MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type {
  MeshCoordinateOptions,
  MeshFormat,
  MeshLoadResult,
  MeshPrimitive,
} from '@/plugins/mesh/types'

/** 加载期间只借用 Geometry/Texture，完成时向同一根节点交接最终结果。 */
export class MeshLoadingPreview implements MeshLoadResult {
  readonly root = new THREE.Group()
  readonly boundingBox = new THREE.Box3()
  readonly resources = new MeshResourceTracker()
  private readonly parts: MeshPrimitive[] = []
  private readonly sources = new Map<string, readonly MeshPrimitive[]>()
  private readonly materials = new Set<THREE.Material>()
  private finalResult?: MeshLoadResult
  private partialReport: MeshLoadResult['report']

  constructor(
    readonly format: MeshFormat,
    name: string
  ) {
    this.root.name = name
    this.root.matrixAutoUpdate = false
    this.partialReport = {
      elapsedMs: 0,
      diagnostics: { ignoredAnimations: [], warnings: [] },
      stats: {
        format,
        primitiveCount: 0,
        triangleCount: 0,
        vertexCount: 0,
        materialCount: 0,
        opaqueCount: 0,
        maskCount: 0,
        blendCount: 0,
      },
    }
  }

  get primitives() {
    return this.finalResult?.primitives ?? this.parts
  }
  get sourcePrimitiveMap() {
    return this.finalResult?.sourcePrimitiveMap ?? this.sources
  }
  get report() {
    return this.finalResult?.report ?? this.partialReport
  }
  get diagnostics() {
    return this.report.diagnostics
  }
  get batchSourceMap() {
    return this.finalResult?.batchSourceMap
  }

  /** 追加独立预览对象，在副本上应用坐标，保持加载器源结果不变。 */
  append(part: MeshLoadResult, coordinate?: MeshCoordinateOptions): MeshPrimitive[] {
    const copies = part.primitives.map(
      (primitive): MeshPrimitive => ({
        ...primitive,
        object: primitive.object.clone(false),
        worldMatrix: primitive.worldMatrix.clone(),
        boundingBox: primitive.boundingBox.clone(),
      })
    )
    const batchRoot = new THREE.Group()
    batchRoot.add(...copies.map((primitive) => primitive.object))
    const batch = {
      ...part,
      root: batchRoot,
      primitives: copies,
      boundingBox: part.boundingBox.clone(),
    }
    applyMeshCoordinateOptions(batch, coordinate)
    const stats = { ...this.partialReport.stats }
    for (const primitive of copies) {
      this.root.add(primitive.object)
      this.parts.push(primitive)
      this.boundingBox.union(primitive.boundingBox)
      this.sources.set(primitive.sourceNodeId, [
        ...(this.sources.get(primitive.sourceNodeId) ?? []),
        primitive,
      ])
      this.materials.add(primitive.material)
      stats.primitiveCount += 1
      stats.triangleCount += primitive.triangleCount
      stats.vertexCount += primitive.vertexCount
      if (primitive.renderClass === 'opaque') stats.opaqueCount += 1
      else if (primitive.renderClass === 'mask') stats.maskCount += 1
      else stats.blendCount += 1
    }
    stats.materialCount = this.materials.size
    this.partialReport = { ...part.report, stats }
    return copies
  }

  /** 保留宿主设置的根节点变换，原子交接完整结果。 */
  complete(result: MeshLoadResult): void {
    this.root.clear()
    for (const child of [...result.root.children]) this.root.add(child)
    this.boundingBox.copy(result.boundingBox)
    this.finalResult = result
    this.parts.length = 0
    this.sources.clear()
    this.materials.clear()
  }

  /** 部分结果不拥有源资源；完整结果交由原 Loader 幂等释放。 */
  dispose(): void {
    this.root.clear()
    this.parts.length = 0
    this.sources.clear()
    this.materials.clear()
    this.finalResult?.dispose()
  }
}
